import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import i18n from 'i18next';
import Cookies from 'js-cookie';
import { fetchOrganizationInfo } from '../slices/organizationInfo';
import { IOrganizationInfo, OrganizationMetadataType } from '../interfaces/organizationInfo';
import { localeCookie } from '@pec/aion-ui-i18next/constants';
import { changePreferredCultureCode } from '@pec/aion-ui-i18next/helpers/changePreferredCultureCode';

/**
 * Safety-net hook: runs once after login and applies the organisation's preferred
 * language when the user has no persisted language preference of their own.
 *
 * This is a no-op for users whose culture was already set by the billing-service at
 * account creation (Steps 1.2 / 1.3). It only activates when a user lands on the
 * platform without a `.culture` cookie or `userSelectedLng` localStorage entry.
 *
 * Usage: call `useOrgLanguageBootstrap(orgId, userId)` in the top-level app component.
 */
export const useOrgLanguageBootstrap = (organizationId: string | undefined, userId: string | undefined): void => {
  const dispatch = useDispatch();
  const organizationInfo = useSelector(
    (state: { organizationInfo?: { organizationInfo?: IOrganizationInfo | null } }) =>
      state.organizationInfo?.organizationInfo
  );

  useEffect(() => {
    if (!organizationId || !userId) return;
    const hasUserPreference = !!Cookies.get(localeCookie) || !!localStorage.getItem('userSelectedLng');
    if (hasUserPreference) return;

    dispatch(fetchOrganizationInfo(organizationId));
  }, [dispatch, organizationId, userId]);

  useEffect(() => {
    if (!organizationInfo || !userId) return;
    const hasUserPreference = !!Cookies.get(localeCookie) || !!localStorage.getItem('userSelectedLng');
    if (hasUserPreference) return;

    const preferredLang = organizationInfo.organizationMetadatas?.find(
      (m: { typeId: OrganizationMetadataType; value: string | null }) =>
        m.typeId === OrganizationMetadataType.PreferredLanguage
    )?.value;

    if (preferredLang) {
      i18n.changeLanguage(preferredLang);
      localStorage.setItem('userSelectedLng', preferredLang);
      changePreferredCultureCode(userId, preferredLang);
    }
  }, [organizationInfo, userId]);
};
